import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Button, FlexRow, Grid, IconSize, IconType, ModalSubstrate } from 'ui-toolkit';
import { RotateIcon } from '../../RotateIcon.styled';
import {
  StyledItemMetaDataModal,
  StyledItemMetaDataModalCard,
  StyledItemMetaDataModalCardLabel,
  StyledItemMetaDataModalCell,
} from './ItemMetaDataModal.styled';

export interface IItemMetaDataModalProps extends React.ComponentPropsWithoutRef<'div'> {
  expanded?: boolean;
  expandedLabel?: React.ReactNode | string;
  label: React.ReactNode | string;
  onToggle?: (expanded: boolean) => void;
}

export interface IItemMetaDataModalRef {
  modalContains: (element: HTMLElement) => boolean;
}

const ItemMetaDataModal = React.forwardRef<IItemMetaDataModalRef, IItemMetaDataModalProps>(
  ({ children, expanded, expandedLabel, label, onToggle }, ref) => {
    const [expandedState, setExpandedState] = React.useState(expanded);
    const [prevExpandedProp, setPrevExpandedProp] = React.useState(expanded);
    const gridRow = React.useRef<HTMLDivElement>(null);
    const toggleExpansion = () => setExpandedState(!expandedState);

    if (expanded !== prevExpandedProp) {
      setExpandedState(expanded);
      setPrevExpandedProp(expanded);
    }

    React.useImperativeHandle(ref, () => ({
      modalContains: (element: HTMLElement) => (gridRow.current ? !!gridRow.current.contains(element) : false),
    }));

    useEffect(() => {
      if (onToggle) {
        onToggle(expandedState === true);
      }
    }, [expandedState, onToggle]);

    const StyledButton = styled(Button)`
      min-width: 32px;
    `;

    return (
      <StyledItemMetaDataModal className="item-meta-data-modal">
        <StyledItemMetaDataModalCell className={'ItemMetaDataModalRowStyled'} onClick={toggleExpansion}>
          <span>{label}</span>
          <Button intent="ghost" isInline onClick={toggleExpansion}>
            <RotateIcon type={IconType.CaretDd} size={IconSize.Small} isRotated={false} />
          </Button>
        </StyledItemMetaDataModalCell>
        {expandedState ? (
          <>
            <ModalSubstrate onClick={toggleExpansion} />
            <StyledItemMetaDataModalCard modal={true}>
              <Grid ref={gridRow}>
                <FlexRow justifyContent={'space-between'} width={100}>
                  <StyledItemMetaDataModalCardLabel>{expandedLabel || label}</StyledItemMetaDataModalCardLabel>
                  <StyledButton intent="ghost" isInline onClick={toggleExpansion} data-test-id={`close-metadata`}>
                    <RotateIcon type={IconType.CaretDd} size={IconSize.Small} isRotated={true} />
                  </StyledButton>
                </FlexRow>
                <div className="item-meta-data-modal__content">{children}</div>
              </Grid>
            </StyledItemMetaDataModalCard>
          </>
        ) : null}
      </StyledItemMetaDataModal>
    );
  }
);

export default ItemMetaDataModal;
